/**
 * SankalpaPage — real React replacement for the /sankalpa iframe mockup.
 *
 * Staged at /sankalpa-v2. /sankalpa continues to serve the iframe mockup
 * until the founder authorises the flip.
 *
 * Consumes System B (POST /api/mycraftz-install/ideas) — same call Bodha
 * makes; hits the backend cache instantly when the user has already been
 * through Bodha for this book. Renders the 10 ideas as togglable cards
 * that enforce the Rule of Three (exactly 3 commitments).
 *
 * URL contract:
 *   /sankalpa-v2                       → empty state (designed, no redirect)
 *   /sankalpa-v2?book=<numeric-id>     → primary path
 *   /sankalpa-v2?book=<slug>           → legacy path (accepted)
 *   ?mode=preview | ?prod=1            → ignored (v1)
 *
 * Local storage (icraftz.sankalpa.commit.v1):
 *   { "<bookId>": { committed: [id, id, id], committedAt: "<ISO>" } }
 *
 * Also reads (never writes) icraftz.bodha.choices.v1[<bookId>].resonates
 * to pre-highlight ideas the user resonated with in Bodha (advisory only).
 *
 * Every mount re-reads BOTH keys from localStorage keyed on the CURRENT
 * bookId — never derived from component lifetime, never carried forward
 * from a previous book. This is intentional (see BUILD SPEC §8).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check, RefreshCw, Sparkles } from "lucide-react";

import Navigation from "../components/homev3/Navigation";
import Footer from "../components/homev3/Footer";
import GoldRule from "../components/homev3/GoldRule";
import ParchmentBackground from "../components/ParchmentBackground";
import { useLanguage } from "../lib/useLanguage";
import { withTransientRetry } from "../lib/apiRetry";
import { T, R } from "../components/homev3/tokens";
import {
  CARD_BG,
  CARD_BORDER,
  CARD_RADIUS,
  CARD_SHADOW,
  CARD_SHADOW_HOVER,
  StageKicker,
  StageLeftIllustration,
  StageEmptyStateFlank,
} from "../components/homev3/parchmentCard";
import { resolveBook } from "../lib/bookLookup";
import { useAuth } from "../context/AuthContext";
import InfographicEntryButton from "../components/InfographicEntryButton";
import { translatingLine } from "../lib/translatingLine";

// ─────────────────────────────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────────────────────────────

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/mycraftz-install`;
const LS_SANKALPA = "icraftz.sankalpa.commit.v1";
const LS_BODHA    = "icraftz.bodha.choices.v1";
const IDEAS_COUNT = 10;
const RULE_OF_N   = 3;
const AXIOS_TIMEOUT_MS = 60000;

// ─────────────────────────────────────────────────────────────────────
//  LocalStorage helpers (SSR-safe; ALWAYS read fresh per mount)
// ─────────────────────────────────────────────────────────────────────

function readAll(key) {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(key, all) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(all));
  } catch {
    /* quota or private-mode — safely ignore */
  }
}

/** Fresh read of the Bodha resonates set for a given book id. */
function readBodhaResonates(bookId) {
  const all = readAll(LS_BODHA);
  const entry = all[String(bookId)] || {};
  return Array.isArray(entry.resonates) ? entry.resonates : [];
}

/** Fresh read of the Sankalpa commit entry for a given book id. */
function readSankalpaCommit(bookId) {
  const all = readAll(LS_SANKALPA);
  const entry = all[String(bookId)] || {};
  return {
    committed:   Array.isArray(entry.committed) ? entry.committed : [],
    committedAt: typeof entry.committedAt === "string" ? entry.committedAt : null,
  };
}

/** Persist Sankalpa commit (or clear on empty array). */
function writeSankalpaCommit(bookId, committed) {
  const all = readAll(LS_SANKALPA);
  if (committed.length === 0) {
    delete all[String(bookId)];
  } else {
    all[String(bookId)] = {
      committed,
      committedAt: new Date().toISOString(),
    };
  }
  writeAll(LS_SANKALPA, all);
}

// ─────────────────────────────────────────────────────────────────────
//  Presentational primitives
// ─────────────────────────────────────────────────────────────────────

function Diya() {
  return (
    <div
      aria-hidden="true"
      style={{ display: "inline-flex", alignItems: "flex-end", gap: 6, verticalAlign: "middle" }}
    >
      <span
        style={{
          display: "inline-block",
          width: 10,
          height: 14,
          borderRadius: "50% 50% 45% 45% / 60% 60% 40% 40%",
          background: "radial-gradient(circle at 50% 35%, #FFE9A6 0%, #E4A82C 55%, #8B5A22 100%)",
          animation: "sankalpa-flicker 1.6s ease-in-out infinite alternate",
          transformOrigin: "50% 100%",
        }}
      />
      <span
        style={{
          display: "inline-block",
          width: 26,
          height: 6,
          borderRadius: "50%",
          background: "linear-gradient(180deg, #6b3d1a 0%, #3a1e0c 100%)",
          boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.35)",
        }}
      />
      <style>{`
        @keyframes sankalpa-flicker {
          0%   { transform: scale(1)   translateY(0);    filter: brightness(1); }
          50%  { transform: scale(1.06) translateY(-1px); filter: brightness(1.12); }
          100% { transform: scale(0.98) translateY(1px);  filter: brightness(0.94); }
        }
      `}</style>
    </div>
  );
}

function skelBar(widthPct, height, mb) {
  return {
    width: `${widthPct}%`,
    height,
    marginBottom: mb,
    borderRadius: 4,
    background:
      "linear-gradient(90deg, rgba(139,99,71,0.08) 0%, rgba(139,99,71,0.18) 50%, rgba(139,99,71,0.08) 100%)",
    backgroundSize: "400px 100%",
    animation: "sankalpa-shimmer 1.4s ease-in-out infinite",
  };
}

// ─────────────────────────────────────────────────────────────────────
//  Idea card — toggle-selectable with Rule-of-Three enforcement
// ─────────────────────────────────────────────────────────────────────

function IdeaCard({ idea, index, selected, disabled, resonated, onToggle, isSkeleton }) {
  const [hover, setHover] = useState(false);
  const canInteract = !disabled || selected; // selected cards can always deselect

  const cardStyle = {
    position: "relative",
    background: selected ? "rgba(240,230,204,0.95)" : CARD_BG,
    border: selected ? "2px solid #8B3A12" : CARD_BORDER,
    borderRadius: CARD_RADIUS,
    padding: "20px 20px 20px",
    boxShadow: selected
      ? "0 8px 22px rgba(139,58,18,0.25)"
      : hover && canInteract
      ? CARD_SHADOW_HOVER
      : CARD_SHADOW,
    transform: hover && canInteract ? "translateY(-2px)" : "translateY(0)",
    transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, opacity 180ms ease",
    minHeight: 190,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 14,
    opacity: disabled && !selected ? 0.42 : 1,
    cursor: isSkeleton ? "default" : canInteract ? "pointer" : "not-allowed",
    userSelect: "none",
  };

  if (isSkeleton) {
    return (
      <div style={{ ...cardStyle, cursor: "default", transform: "none" }} aria-hidden="true">
        <div>
          <div style={skelBar(90, 14, 10)} />
          <div style={skelBar(70, 18, 14)} />
          <div style={skelBar(100, 12, 6)} />
          <div style={skelBar(85, 12, 6)} />
        </div>
        <div style={skelBar(40, 22, 0)} />
        <style>{`
          @keyframes sankalpa-shimmer {
            0%   { background-position: -200px 0; }
            100% { background-position: 200px 0; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      data-testid={`sankalpa-idea-${idea.id}`}
      data-selected={selected}
      data-resonated={resonated || undefined}
      style={cardStyle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => canInteract && onToggle(idea.id)}
      role="button"
      tabIndex={canInteract ? 0 : -1}
      aria-pressed={selected}
      aria-disabled={!canInteract}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && canInteract) {
          e.preventDefault();
          onToggle(idea.id);
        }
      }}
    >
      {/* Resonated in Bodha badge */}
      {resonated && (
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px",
            borderRadius: 999,
            background: "rgba(201,168,76,0.20)",
            border: `1px solid ${T.GOLD}`,
            fontFamily: T.F_HEADING,
            fontSize: 9,
            letterSpacing: "0.12em",
            color: T.DARK_BROWN,
          }}
          title="You resonated with this in Bodha"
        >
          <Sparkles size={9} strokeWidth={2} /> RESONATED
        </div>
      )}

      {/* Ordinal + title + description */}
      <div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
          <span
            style={{
              fontFamily: T.F_HEADING,
              fontSize: 11,
              letterSpacing: "0.16em",
              color: T.GOLD,
            }}
          >
            {String(index + 1).padStart(2, "0")}
          </span>
          <span style={{ flex: 1, height: 1, background: `linear-gradient(to right, ${T.GOLD}, transparent)` }} />
        </div>
        <h3
          className="m-0"
          style={{
            fontFamily: T.F_HEADING,
            fontSize: 18,
            fontWeight: 600,
            color: T.DARK_BROWN,
            lineHeight: 1.25,
            marginBottom: 8,
          }}
        >
          {idea.title}
        </h3>
        <p
          className="m-0"
          style={{
            fontFamily: T.F_BODY,
            fontSize: 14,
            fontStyle: "italic",
            color: T.MUTED_BROWN,
            lineHeight: 1.45,
          }}
        >
          {idea.description}
        </p>
      </div>

      {/* Toggle affordance */}
      <button
        type="button"
        data-testid={`sankalpa-idea-toggle-${idea.id}`}
        aria-pressed={selected}
        aria-label={selected ? "Deselect this idea" : "Choose this idea"}
        onClick={(e) => {
          e.stopPropagation();
          if (canInteract) onToggle(idea.id);
        }}
        tabIndex={-1}
        style={{
          alignSelf: "flex-start",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 14px",
          fontFamily: T.F_HEADING,
          fontSize: 11,
          letterSpacing: "0.08em",
          borderRadius: 999,
          border: `1px solid ${selected ? "#8B3A12" : T.GOLD}`,
          background: selected ? "#8B3A12" : "transparent",
          color: selected ? T.PARCHMENT_LT : T.DARK_BROWN,
          cursor: canInteract ? "pointer" : "not-allowed",
          transition: "background 140ms ease, color 140ms ease",
        }}
      >
        {selected ? <><Check size={12} strokeWidth={2} /> Chosen</> : "Choose"}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Left panel — Current Book (mirrors Bodha's panel; commit summary here)
// ─────────────────────────────────────────────────────────────────────

function CurrentBookPanel({ book, selectedCount, committedAt }) {
  return (
    <aside
      data-testid="sankalpa-current-book"
      className="sankalpa-current-book"
      style={{
        background: CARD_BG,
        border: CARD_BORDER,
        borderRadius: CARD_RADIUS,
        padding: 20,
        boxShadow: CARD_SHADOW,
        position: "sticky",
        top: 84,
        maxHeight: "calc(100vh - 108px)",
        minWidth: 0,
      }}
    >
      <p
        className="m-0"
        style={{
          fontFamily: T.F_HEADING,
          fontSize: 11,
          letterSpacing: "0.16em",
          color: T.GOLD,
          textAlign: "center",
          marginBottom: 14,
        }}
      >
        CURRENT BOOK
      </p>

      <div
        style={{
          aspectRatio: "3/4",
          background: "#EADFC4",
          overflow: "hidden",
          borderRadius: 6,
          border: `1px solid ${T.GOLD}`,
          marginBottom: 14,
        }}
      >
        <img
          src={book.coverUrl || book.image}
          alt={book.title}
          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='120'%20height='160'%3E%3Crect%20width='120'%20height='160'%20fill='%23EADFC4'/%3E%3Crect%20x='34'%20y='40'%20width='52'%20height='80'%20fill='none'%20stroke='%23a37e2a'%20stroke-width='2'/%3E%3Cline%20x1='60'%20y1='40'%20x2='60'%20y2='120'%20stroke='%23a37e2a'%20stroke-width='2'/%3E%3C/svg%3E"; }}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </div>

      <h2
        className="m-0"
        style={{
          fontFamily: T.F_HEADING,
          fontSize: 18,
          fontWeight: 700,
          color: T.DARK_BROWN,
          lineHeight: 1.2,
          textAlign: "center",
          marginBottom: 4,
        }}
      >
        {book.title}
      </h2>
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 13,
          color: T.MUTED_BROWN,
          textAlign: "center",
          marginBottom: 14,
        }}
      >
        {book.author}
      </p>

      <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
        <GoldRule />
      </div>

      <p
        className="m-0"
        data-testid="sankalpa-selected-counter"
        style={{
          fontFamily: T.F_BODY,
          fontSize: 13,
          color: T.DARK_BROWN,
          textAlign: "center",
          marginBottom: 6,
        }}
      >
        Chosen <strong>{selectedCount}</strong> / {RULE_OF_N}
      </p>

      {committedAt ? (
        <p
          className="m-0"
          style={{
            fontFamily: T.F_BODY,
            fontStyle: "italic",
            fontSize: 11,
            color: T.MUTED_BROWN,
            textAlign: "center",
            marginBottom: 16,
          }}
        >
          Sealed {new Date(committedAt).toLocaleDateString(undefined, {
            year: "numeric", month: "short", day: "numeric"
          })}
        </p>
      ) : (
        <p
          className="m-0"
          style={{
            fontFamily: T.F_BODY,
            fontStyle: "italic",
            fontSize: 11,
            color: T.MUTED_BROWN,
            textAlign: "center",
            marginBottom: 16,
          }}
        >
          Choose three to seal.
        </p>
      )}

      <InfographicEntryButton book={book} />

      <Link
        to={R.library}
        data-testid="sankalpa-change-book"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          width: "100%",
          padding: "10px 12px",
          fontFamily: T.F_HEADING,
          fontSize: 12,
          letterSpacing: "0.08em",
          color: T.DARK_BROWN,
          textDecoration: "none",
          borderRadius: 999,
          border: `1px solid ${T.GOLD}`,
          background: "transparent",
          transition: "background 140ms ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(201,168,76,0.15)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        Change book <ArrowRight size={12} strokeWidth={1.75} />
      </Link>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Empty state (no ?book= param)
// ─────────────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div
      data-testid="sankalpa-empty-state"
      style={{
        maxWidth: 620,
        margin: "0 auto",
        textAlign: "center",
        padding: "80px 24px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
        <GoldRule wide />
      </div>
      <StageKicker stage="sankalpa" text="SANKALPA · संकल्प" size="large" testId="sankalpa-empty-kicker" />
      <h1
        className="m-0 sankalpa-empty-h1"
        style={{
          fontFamily: T.F_DISPLAY,
          fontSize: 44,
          fontWeight: 700,
          color: T.DARK_BROWN,
          lineHeight: 1.1,
          marginBottom: 14,
        }}
      >
        Choose a book to make a vow.
      </h1>
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 17,
          color: T.MUTED_BROWN,
          lineHeight: 1.55,
          marginBottom: 26,
        }}
      >
        Sankalpa is where you distill ten ideas into three that will shape
        your practice. Pick any book from the Library to begin.
      </p>
      <Link
        to={R.library}
        data-testid="sankalpa-empty-cta"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 26px",
          fontFamily: T.F_HEADING,
          fontSize: 13,
          letterSpacing: "0.10em",
          color: T.PARCHMENT_LT,
          textDecoration: "none",
          borderRadius: 999,
          background: T.DARK_BROWN,
          border: `1px solid ${T.DARK_BROWN}`,
          boxShadow: "0 3px 10px rgba(44,24,16,0.20)",
        }}
      >
        Explore the Library <ArrowRight size={14} strokeWidth={1.75} />
      </Link>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Error state
// ─────────────────────────────────────────────────────────────────────

function ErrorState({ bookTitle, onRetry }) {
  return (
    <div
      data-testid="sankalpa-error-state"
      style={{
        maxWidth: 520,
        margin: "60px auto",
        textAlign: "center",
        padding: "40px 24px",
        background: CARD_BG,
        border: CARD_BORDER,
        borderRadius: CARD_RADIUS,
        boxShadow: CARD_SHADOW,
      }}
    >
      <p
        className="m-0"
        style={{
          fontFamily: T.F_HEADING,
          fontSize: 11,
          letterSpacing: "0.18em",
          color: "#8B3A12",
          marginBottom: 10,
        }}
      >
        UNABLE TO LOAD IDEAS
      </p>
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 15,
          color: T.MUTED_BROWN,
          marginBottom: 22,
        }}
      >
        The oracle went quiet while consulting {bookTitle}. Try again in a moment.
      </p>
      <button
        type="button"
        data-testid="sankalpa-retry"
        onClick={onRetry}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 22px",
          fontFamily: T.F_HEADING,
          fontSize: 12,
          letterSpacing: "0.10em",
          color: T.PARCHMENT_LT,
          background: T.DARK_BROWN,
          border: `1px solid ${T.DARK_BROWN}`,
          borderRadius: 999,
          cursor: "pointer",
        }}
      >
        <RefreshCw size={13} strokeWidth={1.75} /> Try again
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Loading banner (with diya)
// ─────────────────────────────────────────────────────────────────────

function LoadingBanner({ bookTitle }) {
  return (
    <div
      data-testid="sankalpa-loading-banner"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        margin: "0 auto 22px",
        padding: "10px 22px",
        maxWidth: 620,
        background: "rgba(240,230,204,0.6)",
        border: `1px dashed ${T.GOLD}`,
        borderRadius: 999,
      }}
    >
      <Diya />
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 14,
          color: T.DARK_BROWN,
        }}
      >
        Preparing your ten ideas from <strong>{bookTitle}</strong>…
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Main page
// ─────────────────────────────────────────────────────────────────────

export default function SankalpaPage() {
  const [params] = useSearchParams();
  const bookParam = params.get("book");
  const book = useMemo(() => resolveBook(bookParam), [bookParam]);

  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Language-aware artefact translation ──────────────────────────
  // Ideas are always fetched in English from /ideas (their ids drive
  // the commit LS entry and downstream Sadhana state). When the user
  // has a non-EN language selected, we call /ideas-translate and merge
  // title/description onto the English rows — preserving ids so
  // resonance highlighting and selection state stay stable.
  const [lang] = useLanguage();
  const { isAuthenticated, authHeaders } = useAuth();
  const [sealSync, setSealSync] = useState("idle"); // idle | saving | saved | error
  const [translatedIdeas, setTranslatedIdeas] = useState(null);
  const [isTranslatingIdeas, setIsTranslatingIdeas] = useState(false);
  const translateCacheRef = useRef({}); // { `${bookId}::${lang}` → ideas[] }

  // Selection state (in-memory only until commit)
  const [selected, setSelected] = useState([]);           // array of idea ids, order = selection order
  const [committedAt, setCommittedAt] = useState(null);   // ISO string when this book's commit was sealed
  const [resonated, setResonated] = useState([]);         // Bodha resonates for THIS book (read-only)

  // Track which book's LS entry we've already hydrated (prevents stale reads
  // if book flips while ideas are still loading).
  const hydratedFor = useRef(null);

  // ── Fresh read of BOTH LS keys on EVERY book change ───────────────
  useEffect(() => {
    if (!book) {
      hydratedFor.current = null;
      setSelected([]);
      setCommittedAt(null);
      setResonated([]);
      return;
    }
    const commit = readSankalpaCommit(book.id);
    const bodhaResonates = readBodhaResonates(book.id);
    setSelected(commit.committed);
    setCommittedAt(commit.committedAt);
    setResonated(bodhaResonates);
    hydratedFor.current = String(book.id);
    if (!isAuthenticated) return;
    // Server is the source of truth for Bodha picks and the seal.
    let alive = true;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/mycraftz-install/journey-state`, {
      params: { book_id: book.id, targetLanguage: lang }, headers: authHeaders(),
    }).then((r) => {
      if (!alive) return;
      const srvRes = r.data?.bodha?.resonates || [];
      if (srvRes.length) setResonated(srvRes);
      const seal = r.data?.seal;
      if (seal && Array.isArray(seal.idea_ids) && seal.idea_ids.length === RULE_OF_N) {
        setSelected(seal.idea_ids);
        setCommittedAt(seal.sealed_at);
        const all = readAll(LS_SANKALPA);
        all[String(book.id)] = { committed: seal.idea_ids, committedAt: seal.sealed_at };
        writeAll(LS_SANKALPA, all);
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, [book, isAuthenticated, lang, authHeaders]);

  // ── Fetch 10 ideas — once per book id per mount ───────────────────
  const fetchIdeas = useCallback(async () => {
    if (!book) return;
    setLoading(true);
    setError(null);
    setIdeas([]);
    const snapshot = {
      id:          String(book.id),
      title:       book.title || "",
      author:      book.author || "",
      summary:     book.summary || "",
      insights:    Array.isArray(book.insights)    ? book.insights    : [],
      actionItems: Array.isArray(book.actionItems) ? book.actionItems : [],
      coreTheme:   book.coreTheme || "",
    };
    try {
      const res = await axios.post(
        `${API_BASE}/ideas`,
        { book: snapshot },
        { timeout: AXIOS_TIMEOUT_MS }
      );
      const list = Array.isArray(res.data?.ideas) ? res.data.ideas : [];
      setIdeas(list.slice(0, IDEAS_COUNT));
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [book]);

  useEffect(() => {
    if (!book) return;
    fetchIdeas();
  }, [book, fetchIdeas]);

  // ── Translate ideas when a non-English language is active ────────
  useEffect(() => {
    if (!book) return;
    if (!Array.isArray(ideas) || ideas.length === 0) {
      setTranslatedIdeas(null);
      setIsTranslatingIdeas(false);
      return;
    }
    if (lang === "en") {
      setTranslatedIdeas(null);
      setIsTranslatingIdeas(false);
      return;
    }
    const cacheKey = `${book.id}::${lang}`;
    if (translateCacheRef.current[cacheKey]) {
      setTranslatedIdeas(translateCacheRef.current[cacheKey]);
      setIsTranslatingIdeas(false);
      return;
    }
    let cancelled = false;
    setIsTranslatingIdeas(true);
    (async () => {
      try {
        const res = await withTransientRetry(() =>
          axios.post(
            `${API_BASE}/ideas-translate`,
            {
              book_id: String(book.id),
              book_title: book.title || "",
              targetLanguage: lang,
              ideas: ideas.map((i) => ({
                id: i.id,
                title: i.title || "",
                description: i.description || "",
              })),
            },
            { timeout: AXIOS_TIMEOUT_MS }
          )
        );
        if (cancelled) return;
        const payload = res.data;
        if (payload?.lang === lang && Array.isArray(payload.ideas)) {
          translateCacheRef.current[cacheKey] = payload.ideas;
          setTranslatedIdeas(payload.ideas);
        } else {
          // Fail-open — leave English rendered.
          setTranslatedIdeas(null);
        }
      } catch (e) {
        if (!cancelled) {
          setTranslatedIdeas(null);
        }
      } finally {
        if (!cancelled) setIsTranslatingIdeas(false);
      }
    })();
    return () => { cancelled = true; };
  }, [book, ideas, lang]);

  // Effective ideas to render — translated title/description overlayed
  // on the English rows, keyed by stable idea id.
  const displayIdeas = useMemo(() => {
    if (!translatedIdeas || !Array.isArray(ideas) || ideas.length === 0) {
      return ideas;
    }
    const byId = new Map(translatedIdeas.map((t) => [Number(t.id), t]));
    return ideas.map((eng) => {
      const tr = byId.get(Number(eng.id));
      return tr
        ? { ...eng, title: tr.title || eng.title, description: tr.description || eng.description }
        : eng;
    });
  }, [ideas, translatedIdeas]);

  // ── Toggle handler with Rule-of-Three enforcement ─────────────────
  const canPickMore = selected.length < RULE_OF_N;

  const handleToggle = useCallback(
    (ideaId) => {
      setSelected((prev) => {
        if (prev.includes(ideaId)) {
          return prev.filter((x) => x !== ideaId);
        }
        if (prev.length >= RULE_OF_N) {
          return prev; // guard — cannot select a 4th
        }
        return [...prev, ideaId];
      });
    },
    []
  );

  // ── Commit / re-commit ────────────────────────────────────────────
  const canCommit = selected.length === RULE_OF_N && !loading && !error;

  const handleCommit = useCallback(async () => {
    if (!book || !canCommit) return;
    writeSankalpaCommit(book.id, selected);
    setCommittedAt(new Date().toISOString());
    if (!isAuthenticated) return;
    // Persist the seal server-side immediately (not only when Sadhana opens).
    setSealSync("saving");
    try {
      await withTransientRetry(() => axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/mycraftz-install/sankalpa/seal`,
        {
          book: { id: String(book.id), title: book.title, author: book.author || "" },
          idea_ids: selected,
          targetLanguage: lang,
        },
        { headers: authHeaders() }
      ));
      setSealSync("saved");
    } catch (e) {
      setSealSync("error");
    }
  }, [book, canCommit, selected, isAuthenticated, authHeaders, lang]);

  // Reset current selection (does NOT clear the persisted commit until
  // the user picks 3 again and commits — mirrors "change your Sankalpa").
  const handleClearSelection = useCallback(() => {
    setSelected([]);
  }, []);

  // ────────────────────────────────────────────────────────────────
  //  RENDER
  // ────────────────────────────────────────────────────────────────

  const isAlreadyCommitted = Boolean(committedAt) && selected.length === RULE_OF_N;

  return (
    <div
      data-testid="sankalpa-page"
      className="min-h-screen flex flex-col sankalpa-page-root"
      style={{ background: "transparent" }}
    >
      <ParchmentBackground />
      <Navigation activePath={R.sankalpa} />

      <main
        className="flex-1 w-full"
        style={{ paddingTop: 88, paddingBottom: 64, background: "transparent" }}
      >
        {!book ? (
          <div className="sankalpa-empty-wrap" style={{ position: "relative", minHeight: 864 }}>
            <StageEmptyStateFlank stage="sankalpa" />
            <EmptyState />
          </div>
        ) : (
          <div
            className="sankalpa-shell"
            style={{
              position: "relative",
              maxWidth: 1240,
              margin: "0 auto",
              padding: "0 32px",
              display: "grid",
              gridTemplateColumns: "260px 1fr",
              gap: 40,
              alignItems: "flex-start",
            }}
          >
            <div
              className="sankalpa-left-col"
              style={{
                alignSelf: "stretch",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CurrentBookPanel
                book={book}
                selectedCount={selected.length}
                committedAt={committedAt}
              />
              <StageLeftIllustration stage="sankalpa" />
            </div>

            <section>
              {/* Header */}
              <div style={{ textAlign: "center", marginBottom: 22 }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                  <GoldRule wide />
                </div>
                <StageKicker stage="sankalpa" text="SANKALPA · संकल्प" testId="sankalpa-header-kicker" />
                <h1
                  className="m-0 sankalpa-header-h1"
                  style={{
                    fontFamily: T.F_DISPLAY,
                    fontSize: 40,
                    fontWeight: 700,
                    color: T.DARK_BROWN,
                    lineHeight: 1.1,
                    marginBottom: 10,
                  }}
                >
                  Choose the three that will change you.
                </h1>
                <p
                  className="m-0"
                  style={{
                    fontFamily: T.F_BODY,
                    fontStyle: "italic",
                    fontSize: 16,
                    color: T.MUTED_BROWN,
                    lineHeight: 1.55,
                    maxWidth: 620,
                    margin: "0 auto 6px",
                  }}
                >
                  From the ten ideas of <strong>{book.title || "this book"}</strong>, commit to
                  exactly <strong>three</strong>. These are the ideas your Sadhana
                  will be built around.
                </p>
                {resonated.length > 0 && (
                  <p
                    className="m-0"
                    style={{
                      fontFamily: T.F_BODY,
                      fontStyle: "italic",
                      fontSize: 12,
                      color: T.GOLD,
                      letterSpacing: "0.04em",
                      marginTop: 6,
                    }}
                  >
                    <Sparkles size={11} strokeWidth={2} style={{ display: "inline", verticalAlign: "-1px", marginRight: 4 }} />
                    {resonated.length} idea{resonated.length === 1 ? "" : "s"} you resonated with in Bodha are highlighted.
                  </p>
                )}
              </div>

              {loading && <LoadingBanner bookTitle={book.title} />}
              {error && !loading && (
                <ErrorState bookTitle={book.title} onRetry={fetchIdeas} />
              )}

              {/* Translation status — shown only while /ideas-translate
                  is in flight. Silent on English and after the cache
                  resolves. Matches the parchment aesthetic used on
                  BodhaPage + BookDetailPage. */}
              {!loading && !error && lang !== "en" && isTranslatingIdeas && (
                <div
                  data-testid="sankalpa-translating-banner"
                  style={{
                    margin: "0 auto 16px",
                    padding: "8px 14px",
                    maxWidth: 640,
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    fontSize: "0.95rem",
                    fontStyle: "italic",
                    color: "#5C4A3A",
                    background: "rgba(201,168,76,0.10)",
                    border: "1px solid rgba(201,168,76,0.45)",
                    borderRadius: 4,
                    textAlign: "center",
                  }}
                >
                  {translatingLine(lang)}
                </div>
              )}

              {(loading || ideas.length > 0) && !error && (
                <div
                  data-testid="sankalpa-idea-grid"
                  className="grid gap-5"
                  style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}
                >
                  {loading
                    ? Array.from({ length: IDEAS_COUNT }).map((_, i) => (
                        <IdeaCard key={`skel-${i}`} isSkeleton />
                      ))
                    : displayIdeas.map((idea, i) => {
                        const isSelected = selected.includes(idea.id);
                        return (
                          <IdeaCard
                            key={idea.id}
                            idea={idea}
                            index={i}
                            selected={isSelected}
                            disabled={!canPickMore && !isSelected}
                            resonated={resonated.includes(idea.id)}
                            onToggle={handleToggle}
                          />
                        );
                      })}
                </div>
              )}

              {/* Commit CTA + Sadhana safeguard */}
              {ideas.length > 0 && !loading && !error && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 12,
                    marginTop: 44,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {selected.length > 0 && !isAlreadyCommitted && (
                      <button
                        type="button"
                        onClick={handleClearSelection}
                        data-testid="sankalpa-clear-selection"
                        style={{
                          padding: "10px 20px",
                          fontFamily: T.F_HEADING,
                          fontSize: 12,
                          letterSpacing: "0.08em",
                          color: T.DARK_BROWN,
                          background: "transparent",
                          border: `1px solid ${T.GOLD}`,
                          borderRadius: 999,
                          cursor: "pointer",
                        }}
                      >
                        Clear
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleCommit}
                      disabled={!canCommit}
                      aria-disabled={!canCommit}
                      data-testid="sankalpa-commit-cta"
                      title={
                        canCommit
                          ? isAlreadyCommitted
                            ? "Re-seal your Sankalpa with this selection"
                            : "Seal your Sankalpa"
                          : `Choose ${RULE_OF_N - selected.length} more to commit`
                      }
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "12px 28px",
                        fontFamily: T.F_HEADING,
                        fontSize: 13,
                        letterSpacing: "0.10em",
                        color: canCommit ? T.PARCHMENT_LT : "rgba(61,35,20,0.55)",
                        background: canCommit ? T.DARK_BROWN : "rgba(240,230,204,0.55)",
                        border: `${canCommit ? 1 : 1}px ${canCommit ? "solid" : "dashed"} ${canCommit ? T.DARK_BROWN : T.GOLD}`,
                        borderRadius: 999,
                        cursor: canCommit ? "pointer" : "not-allowed",
                        boxShadow: canCommit ? "0 3px 10px rgba(44,24,16,0.20)" : "none",
                        transition: "background 160ms ease, color 160ms ease",
                      }}
                    >
                      {isAlreadyCommitted && selected.length === RULE_OF_N ? "Re-seal Sankalpa" : "Seal Sankalpa"}
                      <ArrowRight size={14} strokeWidth={1.75} />
                    </button>
                    {sealSync === "error" && (
                      <p role="alert" data-testid="sankalpa-seal-sync-error" className="m-0" style={{ fontFamily: T.F_BODY, fontSize: 13, fontStyle: "italic", color: "#8B3A2E" }}>
                        Sealed on this device, but we couldn&apos;t sync it. <button type="button" onClick={handleCommit} style={{ background: "none", border: 0, padding: 0, textDecoration: "underline", color: "inherit", cursor: "pointer", font: "inherit" }}>Try again</button>
                      </p>
                    )}
                  </div>

                  {/* Live guidance */}
                  <p
                    className="m-0"
                    style={{
                      fontFamily: T.F_BODY,
                      fontStyle: "italic",
                      fontSize: 12,
                      color: T.MUTED_BROWN,
                      letterSpacing: "0.02em",
                    }}
                  >
                    {selected.length < RULE_OF_N
                      ? `Choose ${RULE_OF_N - selected.length} more of the ten to commit.`
                      : isAlreadyCommitted
                      ? "Your Sankalpa is sealed. Change your mind by picking a different three."
                      : "Ready to seal your commitment."}
                  </p>

                  {/* Onward CTA to Sadhana — visible only after seal */}
                  {isAlreadyCommitted && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 8,
                        marginTop: 24,
                        paddingTop: 24,
                        borderTop: `1px dashed ${T.GOLD}`,
                        width: "100%",
                        maxWidth: 520,
                      }}
                    >
                      <p
                        className="m-0"
                        style={{
                          fontFamily: T.F_HEADING,
                          fontSize: 10,
                          letterSpacing: "0.18em",
                          color: T.GOLD,
                          marginBottom: 4,
                        }}
                      >
                        NEXT STEP
                      </p>
                      <Link
                        to={`/sadhana?book=${book.id}`}
                        data-testid="sankalpa-begin-sadhana-cta"
                        title="Begin your daily practice"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "14px 32px",
                          fontFamily: T.F_HEADING,
                          fontSize: 14,
                          letterSpacing: "0.10em",
                          color: T.PARCHMENT_LT,
                          textDecoration: "none",
                          background: T.DARK_BROWN,
                          border: `1px solid ${T.DARK_BROWN}`,
                          borderRadius: 999,
                          boxShadow: "0 3px 12px rgba(44,24,16,0.22)",
                        }}
                      >
                        Begin your Sadhana <ArrowRight size={16} strokeWidth={1.75} />
                      </Link>
                      <p
                        className="m-0"
                        style={{
                          fontFamily: T.F_BODY,
                          fontStyle: "italic",
                          fontSize: 12,
                          color: T.MUTED_BROWN,
                          letterSpacing: "0.02em",
                          textAlign: "center",
                          marginTop: 2,
                        }}
                      >
                        Your three become the seed of a daily practice.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      <Footer activePath={R.sankalpa} />

      {/* ── Mobile responsive pass (<900px) — mirrors Bodha's pattern ── */}
      <style>{`
        .sankalpa-page-root { overflow-x: hidden; }
        @media (max-width: 899px) {
          .stage-flank, .stage-left-illustration, .stage-empty-flank { display: none !important; }
          .sankalpa-empty-wrap { min-height: 0 !important; }
          .sankalpa-shell {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
            padding: 0 20px !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          .sankalpa-shell > * { min-width: 0 !important; }
          .sankalpa-current-book {
            position: static !important;
            max-height: none !important;
            max-width: 320px !important;
            margin: 0 auto !important;
            width: 100% !important;
          }
          [data-testid="sankalpa-idea-grid"] {
            grid-template-columns: 1fr !important;
            gap: 14px !important;
          }
          [data-testid="sankalpa-idea-grid"] > * { min-width: 0 !important; }
          .sankalpa-header-h1 { font-size: 30px !important; line-height: 1.15 !important; }
          .sankalpa-empty-h1  { font-size: 32px !important; }
        }
      `}</style>
    </div>
  );
}
